import { iconNavCheck } from "@bsds-lib-component/icons/icons";
import { BsdsIcon } from "@bsds-lib-component/react";
import React, { useId, useState } from "react";
import { useNavigate } from "react-router-dom";

import packageJson from "../../../../package.json";
import loginImage from "../../assets/login.png";
import { Button, CpfInput, SystemMessage } from "../../components";
import { useForgotUserkey } from "../../hooks/useForgotUserkey";
import type { ForgotUserkeyFormProps } from "../../types/componentType";

const ForgotUserkeyPage: React.FC<ForgotUserkeyFormProps> = ({
  onBackToLogin,
  onLoadingChange,
}) => {
  const navigate = useNavigate();
  const [cpf, setCpf] = useState("");
  const [isCpfValid, setIsCpfValid] = useState(false);
  const {
    isLoading,
    error,
    success,
    step,
    recoveredEmail,
    loadCorporateEmails,
    sendUserkeyReset,
    backToCpfStep,
  } = useForgotUserkey();
  const mainContentId = useId();
  const successMessageId = useId();
  const formId = useId();
  const cpfInputId = useId();
  const errorMessageId = useId();

  React.useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await loadCorporateEmails(cpf);
    } catch {
      // Error handled in hook.
    }
  };

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      await sendUserkeyReset(cpf);
    } catch {
      // Error handled in hook.
    }
  };

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin();
      return;
    }
    navigate("/login", { replace: true });
  };

  const getMaskedEmail = (email: string | null): string => {
    if (!email || !email.includes("@")) return "";
    const [user, domain] = email.split("@");
    if (user.length <= 2) {
      return `${user[0] ?? "*"}*@${domain}`;
    }
    return `${user.slice(0, 2)}${"*".repeat(Math.max(1, user.length - 2))}@${domain}`;
  };

  const renderForgotUserkeyContent = () => {
    if (success) {
      return (
        <div
          id={mainContentId}
          role="region"
          aria-label="Confirmação de recuperação de senha"
        >
          <div className="bsds-text-center bsds-p-16">
            <div
              className="w-34 h-34 bsds-display-flex bsds-align-items-center bsds-justify-content-center rounded-full bg-system-success-200 bsds-text-center mx-auto"
              role="img"
              aria-label="Ícone de sucesso"
            >
              <BsdsIcon
                className="w-12 h-16 text-white"
                icon={iconNavCheck}
                aria-hidden="true"
              />
            </div>
            <h2
              id={successMessageId}
              className="bsds-mb-32 bsds-mt-32 bsds-text-4xl bsds-text-bold"
              role="status"
              aria-live="polite"
            >
              Solicitação Enviada com Sucesso!
            </h2>

            <p className="bsds-text-xs bsds-mb-32" style={{ fontSize: "14px" }}>
              Enviamos um link de recuperação para o e-mail cadastrado com o CPF
              informado.
            </p>

            <div className="bsds-mt-36">
              <Button
                color="primary"
                onClick={handleBackToLogin}
                className="bsds-width-100"
                aria-label="Voltar para a página de login"
              >
                Voltar ao Login
              </Button>
            </div>
            <p className="bsds-mt-8 font-bold" style={{ fontSize: "10px" }}>
              Verifique sua caixa de entrada e spam. O link expira em 48 horas
            </p>
          </div>
        </div>
      );
    }

    if (step === "email") {
      return (
        <div
          id={mainContentId}
          className="bsds-width-100 bsds-flex bsds-flex-col bsds-justify-content-center bsds-items-center bsds-justify-content-center bsds-mt-48"
          role="region"
          aria-label="Selecionar e-mail corporativo para recuperação"
        >
          <div className="bsds-text-center bsds-mb-36">
            <h2
              className="bsds-text-bold bsds-mb-8"
              style={{ fontSize: "36px" }}
            >
              Esqueci minha senha
            </h2>
            <h6
              className="text-text-color bsds-text-sm mt-22"
              style={{ fontSize: "14px" }}
            >
              Clique no botão abaixo para enviar o link de recuperação de senha
              para o e-mail cadastrado com o CPF informado.
            </h6>
            <h6
              className="text-text-color bsds-text-sm mt-8"
              style={{ fontSize: "14px" }}
            >
              Enviar para: {getMaskedEmail(recoveredEmail)}
            </h6>
          </div>

          <div
            className="bsds-width-100 mt-38"
            role="group"
            aria-label="Enviar recuperação de senha para e-mail corporativo"
          >
            <div
              id={errorMessageId}
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <SystemMessage
                message={error || "Erro desconhecido"}
                show={!!error}
                type="error"
              />
            </div>

            <div className="bsds-mt-32">
              <Button
                color="primary"
                type="button"
                className="bsds-width-100"
                disabled={isLoading || !recoveredEmail}
                onClick={() => {
                  void handleSend();
                }}
                aria-label={
                  isLoading
                    ? "Enviando link de recuperação..."
                    : !recoveredEmail
                      ? "Nenhum e-mail disponível para envio"
                      : "Enviar link de recuperação de senha"
                }
                aria-busy={isLoading}
              >
                Enviar
              </Button>
            </div>
            <div className="bsds-mt-16">
              <Button
                type="button"
                color="primary"
                fill="outline"
                onClick={backToCpfStep}
                className="bsds-width-100"
                disabled={isLoading}
                aria-label="Voltar e alterar o CPF"
              >
                Voltar
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        id={mainContentId}
        className="bsds-width-100 bsds-flex bsds-flex-col bsds-justify-content-center bsds-items-center bsds-justify-content-center bsds-mt-48"
        role="region"
        aria-label="Formulário de recuperação de senha"
      >
        <div className="bsds-text-center bsds-mb-36">
          <h2 className="bsds-text-bold bsds-mb-8" style={{ fontSize: "36px" }}>
            Esqueci minha senha
          </h2>
          <h6
            className="text-text-color bsds-text-sm"
            style={{ fontSize: "14px" }}
          >
            Digite seu CPF para receber a recuperação por e-mail.
          </h6>
        </div>

        <form
          id={formId}
          className="bsds-width-100"
          onSubmit={handleContinue}
          aria-label="Formulário de recuperação de senha"
          noValidate
        >
          <div className="bsds-mb-16">
            <div>
              <CpfInput
                value={cpf}
                label="CPF de Acesso"
                onChange={setCpf}
                onValidationChange={setIsCpfValid}
                required
                className="bsds-mt-4"
                aria-label="CPF de acesso (obrigatório)"
                aria-describedby={`${cpfInputId}-help`}
                aria-required
              />
              <span
                id={`${cpfInputId}-help`}
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: "rect(0, 0, 0, 0)", clipPath: "inset(50%)" }}
              >
                Digite seu CPF para receber o link de recuperação de senha por
                e-mail.
              </span>
            </div>
          </div>

          <div
            id={errorMessageId}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <SystemMessage
              message={error || "Erro desconhecido"}
              show={!!error}
              type="error"
            />
          </div>

          <div className="bsds-mt-32">
            <Button
              color="primary"
              type="submit"
              className="bsds-width-100"
              disabled={isLoading || !cpf || !isCpfValid}
              aria-label={
                isLoading
                  ? "Carregando e-mails corporativos..."
                  : !cpf || !isCpfValid
                    ? "Preencha o CPF corretamente para continuar"
                    : "Continuar para selecionar e-mail corporativo"
              }
              aria-busy={isLoading}
            >
              Continuar
            </Button>
          </div>
          <div className="bsds-mt-16">
            <Button
              type="button"
              color="primary"
              fill="outline"
              onClick={handleBackToLogin}
              className="bsds-width-100"
              aria-label="Cancelar e voltar para o login"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen-safe min-w-screen flex items-center justify-center bg-white"
      role="main"
      aria-label="Página de recuperação de senha"
    >
      <div className="w-full max-w-md md:max-w-5xl flex flex-row items-center justify-center md:shadow-custom-border rounded-none md:rounded-lg h-full md:h-auto md:ml-8 md:mr-8">
        <div
          className="w-2/3 hidden md:block"
          role="img"
          aria-label="Ilustração de login"
        >
          <img
            src={loginImage}
            alt=""
            className="w-full h-[612px] object-cover rounded-l-md"
            aria-hidden="true"
          />
        </div>

        <div
          className="w-full md:w-[448px] h-full md:h-[612px] flex items-center justify-center rounded-none md:rounded-r-md relative md:max-w-[448px]"
          role="region"
          aria-label="Formulário de recuperação de senha"
        >
          <div className="w-full h-full md:h-[612px] space-y-6 py-8 px-2 md:px-8 flex flex-col rounded-none md:rounded-l-md mx-auto min-h-screen md:min-h-0 md:w-[448px] md:max-w-[448px]">
            {renderForgotUserkeyContent()}
          </div>
        </div>
      </div>
      <footer
        className="fixed bottom-0 left-0 w-full bg-white py-1 text-center text-xs text-gray-500"
        aria-label="Versão da aplicação"
      >
        v{packageJson.version}
      </footer>
    </div>
  );
};

export default ForgotUserkeyPage;
